import { Capacitor } from "@capacitor/core";

// Per-tab auth/session storage.
//
// On the web we use sessionStorage instead of localStorage so each browser tab
// is an INDEPENDENT session: a user can be signed into a different organization
// in each tab without the tabs overwriting each other's token, and logging out
// in one tab no longer logs the others out. (Trade-off: a brand-new tab starts
// logged out; a refresh within the same tab keeps the session.)
//
// On native (Capacitor) we keep using localStorage, which the app already
// relies on there — native is single-session, so tab isolation doesn't apply.
const isNative = Capacitor.isNativePlatform();

export const sessionGet = (key: string): string | null => {
  try {
    return isNative ? localStorage.getItem(key) : sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const sessionSet = (key: string, value: string): void => {
  try {
    (isNative ? localStorage : sessionStorage).setItem(key, value);
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal */
  }
};

export const sessionRemove = (key: string): void => {
  try {
    if (isNative) {
      localStorage.removeItem(key);
    } else {
      sessionStorage.removeItem(key);
      // Also drop any legacy shared copy a pre-sessionStorage build left behind.
      localStorage.removeItem(key);
    }
  } catch {
    /* non-fatal */
  }
};

// ───────────────────────────────────────────────────────────────────────────
// Cross-tab session inheritance (web only).
//
// Sessions live in per-tab sessionStorage, so tabs stay INDEPENDENT: a user can
// be signed into a different org in each tab, and logging out / in as a
// different user in one tab must NOT disturb the others. The downside is that a
// brand-new tab starts empty and lands on the login page even though another
// tab is already signed in.
//
// To fix ONLY that (without re-coupling tabs), a new/empty tab asks any open tab
// to share its current session (the JWT tokens + user) ONCE at startup and
// copies it into its OWN sessionStorage. From then on the tab is independent —
// later logout / login as a different user only affects this tab.
//
// Transport is BroadcastChannel — an in-memory, same-origin channel. We do NOT
// touch localStorage: the JWT never gets persisted to shared storage (important
// on live), it's only messaged tab→tab and lands in the new tab's sessionStorage
// where the api.ts request interceptor reads it as the Bearer token.
const SYNC_CHANNEL = "teamlio_session_sync";
// Only the JWT-bearing session keys are shared — enough to re-establish auth in
// the new tab; each tab still manages everything else on its own.
const SYNC_KEYS = ["accessToken", "refreshToken", "user"];

// Persistent "last successful login" backup. sessionStorage is wiped when the
// tab closes, so on its own the app would show the landing page after a full
// close+reopen. This localStorage copy lets a reopened / brand-new tab restore
// the session and land on the dashboard. Only the JWT keys are stored, and it's
// preferred AFTER a live open tab (BroadcastChannel) so open tabs stay
// authoritative. Last login wins across tabs.
const BACKUP_KEY = "__teamlio_session_backup__";

// Snapshot this tab's JWT session into the localStorage backup. Call after
// login / token refresh / profile change so persistence stays current.
export const persistSessionBackup = (): void => {
  if (isNative) return;
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) return;
    const data: Record<string, string> = {};
    SYNC_KEYS.forEach((k) => {
      const v = sessionStorage.getItem(k);
      if (v != null) data[k] = v;
    });
    localStorage.setItem(BACKUP_KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable — non-fatal */
  }
};

// Restore the persisted session into THIS tab's sessionStorage when it has none.
// Returns true if a session was restored.
export const restoreSessionBackup = (): boolean => {
  if (isNative) return false;
  try {
    if (sessionStorage.getItem("accessToken")) return false;
    const raw = localStorage.getItem(BACKUP_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as Record<string, string>;
    if (!data?.accessToken) return false;
    Object.entries(data).forEach(([k, v]) => {
      if (typeof v === "string") sessionStorage.setItem(k, v);
    });
    return true;
  } catch {
    return false;
  }
};

// Clear the persisted backup on explicit logout. When `ifToken` is given, only
// clears it if the backup belongs to that session — so logging out one user in
// one tab doesn't wipe a different user's still-active login from persistence.
export const clearSessionBackup = (ifToken?: string | null): void => {
  if (isNative) return;
  try {
    const raw = localStorage.getItem(BACKUP_KEY);
    if (!raw) return;
    if (ifToken) {
      const data = JSON.parse(raw) as Record<string, string>;
      if (data?.accessToken && data.accessToken !== ifToken) return;
    }
    localStorage.removeItem(BACKUP_KEY);
  } catch {
    /* non-fatal */
  }
};

const hasBroadcast = () =>
  typeof window !== "undefined" && typeof (window as any).BroadcastChannel !== "undefined";

// Register once per tab at startup: reply to a new tab's request with THIS tab's
// JWT session — but only if this tab is actually signed in. Kept open for the
// tab's lifetime so any future new tab can inherit.
export const initSessionSyncResponder = (): void => {
  if (isNative || !hasBroadcast()) return;
  try {
    const channel = new BroadcastChannel(SYNC_CHANNEL);
    channel.onmessage = (ev: MessageEvent) => {
      if (!ev?.data || ev.data.type !== "request") return;
      let token: string | null = null;
      try {
        token = sessionStorage.getItem("accessToken");
      } catch {
        return;
      }
      if (!token) return; // not signed in → nothing to share
      const data: Record<string, string> = {};
      try {
        SYNC_KEYS.forEach((k) => {
          const v = sessionStorage.getItem(k);
          if (v != null) data[k] = v;
        });
      } catch {
        return;
      }
      try {
        channel.postMessage({ type: "response", data });
      } catch {
        /* non-fatal */
      }
    };
  } catch {
    /* BroadcastChannel unavailable — non-fatal, tabs just don't inherit */
  }
};

// Called once at startup by a tab BEFORE it decides it's logged out. If this tab
// already has a session (e.g. a same-tab refresh) it's a no-op. Otherwise it
// asks other tabs over BroadcastChannel and, if a signed-in tab answers,
// hydrates its OWN sessionStorage with the JWT. Resolves true when a session was
// inherited, false otherwise (no other signed-in tab).
export const bootstrapSessionFromOtherTabs = (timeoutMs = 300): Promise<boolean> => {
  if (isNative) return Promise.resolve(false);
  try {
    if (sessionStorage.getItem("accessToken")) return Promise.resolve(false);
  } catch {
    return Promise.resolve(false);
  }
  // No cross-tab channel available → skip straight to the persisted backup so a
  // reopened tab still lands on the dashboard.
  if (!hasBroadcast()) return Promise.resolve(restoreSessionBackup());

  return new Promise((resolve) => {
    let channel: BroadcastChannel | null = null;
    let done = false;
    const finish = (restored: boolean) => {
      if (done) return;
      done = true;
      try {
        channel?.close();
      } catch {
        /* non-fatal */
      }
      resolve(restored);
    };

    try {
      channel = new BroadcastChannel(SYNC_CHANNEL);
    } catch {
      resolve(false);
      return;
    }

    channel.onmessage = (ev: MessageEvent) => {
      if (!ev?.data || ev.data.type !== "response") return;
      const data = ev.data.data as Record<string, string> | undefined;
      if (!data || !data.accessToken) return;
      try {
        Object.entries(data).forEach(([k, v]) => {
          if (typeof v === "string") sessionStorage.setItem(k, v);
        });
      } catch {
        /* skip a bad key — non-fatal */
      }
      finish(true);
    };

    try {
      channel.postMessage({ type: "request" });
    } catch {
      // Channel broke → fall back to the persisted backup.
      finish(restoreSessionBackup());
      return;
    }
    // No live tab answered within the window → fall back to the persisted
    // backup (a full close+reopen, or no other signed-in tab).
    setTimeout(() => finish(restoreSessionBackup()), timeoutMs);
  });
};

// IMPORTANT: run the persisted-backup restore SYNCHRONOUSLY at module load —
// this executes while imports resolve, i.e. BEFORE React renders and before any
// api.ts request fires. A brand-new tab therefore already has the JWT in its
// own sessionStorage and never races the async BroadcastChannel handshake (which
// was landing the tab on the dashboard with 401s while a throttled background
// tab was slow to reply). No-op on native, when already signed in, or when there
// is no backup (genuinely logged out). The BroadcastChannel path still runs in
// AuthContext for the rare "no backup but a live tab exists" case.
try {
  restoreSessionBackup();
} catch {
  /* non-fatal */
}
